/**
 * ConfigSummaryChips — Inline clickable config chips (VS Code status bar pattern)
 *
 * Renders a horizontal row of compact chips showing active configuration.
 * Each chip is independently clickable:
 * - First click: opens mini-popover showing all options
 * - Subsequent clicks (popover open): cycles to next value
 * - Chips without options (e.g., docs): action fires immediately
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import ChipPopover from './ChipPopover';
import type { ChipOption } from './ChipPopover';

export type { ChipOption };

export type SubmenuTarget =
  | 'sources'
  | 'model'
  | 'temperature'
  | 'style'
  | 'length'
  | 'features'
  | 'docs';

export interface ChipDef {
  id: SubmenuTarget;
  /** Optional sub-identifier to distinguish chips with the same id (e.g., features/questions vs features/tasks) */
  subId?: string;
  label: string;
  /** Options for the mini-popover (if undefined, no popover is shown) */
  options?: ChipOption[];
  /** Current active value to highlight in the popover */
  activeValue?: string;
  isStrikethrough?: boolean;
}

interface ConfigSummaryChipsProps {
  chips: ChipDef[];
  disabled?: boolean;
  onChipClick: (chip: ChipDef) => void;
  /** Called when user selects an option directly from the popover */
  onOptionSelect?: (chip: ChipDef, value: string) => void;
  /** When true, force-closes any open popover (e.g., when + menu opens) */
  forceClosePopover?: boolean;
}

const ConfigSummaryChips: React.FC<ConfigSummaryChipsProps> = ({
  chips,
  disabled = false,
  onChipClick,
  onOptionSelect,
  forceClosePopover,
}) => {
  const { theme } = useTheme();

  // Popover state: which chip's popover is open + its anchor position
  const [activePopoverKey, setActivePopoverKey] = useState<string | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Force-close popover when parent signals (e.g., + menu opens)
  useEffect(() => {
    if (forceClosePopover) setActivePopoverKey(null);
  }, [forceClosePopover]);

  const handleClose = useCallback(() => {
    setActivePopoverKey(null);
  }, []);

  if (chips.length === 0) return null;

  // Find the active chip for the popover
  const activeChip = activePopoverKey
    ? chips.find((c) => `${c.id}-${c.subId || ''}` === activePopoverKey)
    : null;

  return (
    <div className="relative min-w-0">
      {/* Wrapping chip row */}
      <div className="flex items-center gap-1 min-w-0 flex-wrap">
        {chips.map((chip, idx) => {
          const chipKey = `${chip.id}-${chip.subId || ''}`;
          const isPopoverOpen = activePopoverKey === chipKey;

          // Split "Category: Value" for dual-tone rendering
          const colonIdx = chip.label.indexOf(': ');
          const category = colonIdx > 0 ? chip.label.substring(0, colonIdx) : null;
          const value = colonIdx > 0 ? chip.label.substring(colonIdx + 2) : chip.label;

          return (
            <button
              key={`${chip.id}-${chip.subId || idx}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();

                if (chip.options && chip.options.length > 0) {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setAnchorRect(rect);

                  if (isPopoverOpen) {
                    // Popover already open for this chip → cycle value
                    onChipClick(chip);
                  } else {
                    // First click → just open popover (no cycling)
                    setActivePopoverKey(chipKey);
                  }
                } else {
                  // No options (e.g., docs) → fire action directly
                  onChipClick(chip);
                }
              }}
              disabled={disabled}
              aria-label={chip.label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 min-h-[44px] rounded-full text-xs whitespace-nowrap transition-all cursor-pointer border disabled:opacity-40 disabled:cursor-not-allowed ${
                chip.isStrikethrough
                  ? `opacity-30 hover:opacity-50 border-transparent`
                  : isPopoverOpen
                    ? `opacity-100 bg-white/[0.1] border-white/[0.15]`
                    : `opacity-60 hover:opacity-90 bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08] hover:border-white/[0.12]`
              }`}
              title={chip.label}
            >
              {category && (
                <span className={`font-normal ${theme.colors.text.secondary}`}>{category}</span>
              )}
              <span className={`font-medium ${theme.colors.text.secondary}`}>{value}</span>
            </button>
          );
        })}
      </div>

      {/* Mini-popover for the active chip */}
      {activePopoverKey && anchorRect && activeChip?.options && (
        <ChipPopover
          options={activeChip.options}
          activeValue={activeChip.activeValue || ''}
          anchorRect={anchorRect}
          onSelect={(value) => {
            onOptionSelect?.(activeChip, value);
            setActivePopoverKey(null);
          }}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default React.memo(ConfigSummaryChips);
